/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {useEffect, useRef} from 'react'

// `t` in a useEffect deps array re-fires on any i18next namespace load, not
// just a real language change. For a one-shot side effect (flash alert, SR
// announcement) that needs t there to correct a lazily-loaded namespace's
// text, this stops the unrelated re-fires: fire() only runs when key
// changes, never twice for the same key.
export function useFireOnceForKey(key: string | number | boolean, fire: () => void): void {
  const lastKeyRef = useRef<typeof key | undefined>(undefined)
  useEffect(() => {
    if (lastKeyRef.current === key) return
    lastKeyRef.current = key
    fire()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}
